import {
  AccessTokenResponseData,
  AnyGrant,
  OAuth2Grant,
  OAuth2Service,
  RefreshTokenGrant,
  WAuth2Service,
  AccessTokenData,
  AuthService,
} from '@creditornot/wolt-auth';

const oauth2Service = new OAuth2Service({
  clientId: import.meta.env.VITE_AUTH_CLIENT_ID,
  loginRedirectUri: import.meta.env.VITE_AUTH_LOGIN_REDIRECT_URI,
  logoutRedirectUri: import.meta.env.VITE_AUTH_LOGOUT_REDIRECT_URI,
  serviceUrl: import.meta.env.VITE_AUTH_API_URI,
});
const wauth2Service = new WAuth2Service({ serviceUrl: import.meta.env.VITE_AUTH_API_URI });

function getUsedAuth(): 'oauth' | 'wauth' {
  return localStorage.getItem('authUsed') as 'oauth' | 'wauth';
}

export const authService = {
  oauth(): void {
    localStorage.setItem('authUsed', 'oauth');
  },

  wauth(): void {
    localStorage.setItem('authUsed', 'wauth');
  },

  clean(): void {
    localStorage.removeItem('authUsed');
  },
  isOauth(): boolean {
    return getUsedAuth() === 'oauth';
  },

  isWauth(): boolean {
    return getUsedAuth() === 'wauth';
  },

  woltAuthService: {
    getAuthorizeUri(codeVerifier: string): Promise<string> {
      return oauth2Service.getAuthorizeUri(codeVerifier);
    },

    getLogoutUri(): string {
      return oauth2Service.getLogoutUri();
    },

    async callAccessTokenApi(
      grant: RefreshTokenGrant | AnyGrant | OAuth2Grant,
    ): Promise<AccessTokenResponseData | Error> {
      // Always use WAuth2Service for refresh token operations
      if ('refreshToken' in grant) {
        const result = await wauth2Service.callAccessTokenApi(grant as RefreshTokenGrant);

        // If the result resolves with an error (rather than rejects), it means the refresh token is invalid
        if (result instanceof Error) {
          console.warn('Refresh token invalid:', result);
          localStorage.removeItem('auth-data');
          authService.clean();
          window.location.href = '/login';
        }

        return result;
      }

      // For non-refresh operations, use the appropriate service based on auth mode
      return authService.isOauth()
        ? oauth2Service.callAccessTokenApi(grant as OAuth2Grant)
        : wauth2Service.callAccessTokenApi(grant as AnyGrant);
    },

    callLogoutApi(authData: AccessTokenData) {
      if (authService.isWauth()) {
        return wauth2Service.callLogoutApi(authData);
      }

      return Promise.resolve();
    },

    async authorize() {
      if (authService.isOauth()) {
        return oauth2Service.authorize();
      }

      return 'logged-out';
    },
  } satisfies AuthService<AnyGrant>,
};
